import { Text } from "@ui-kitten/components";
import { ComparisonRowV3 } from "lib/sanity.types";
import React from "react";
import { View } from "react-native";

export interface ComparisonColumn {
  key: string; // product slug (category matrix) or variant key (program matrix)
  label: string;
}

interface Props {
  columns: ComparisonColumn[];
  rows: ComparisonRowV3[];
  // Substitutions for cells flagged perVariant (e.g. the selected Family
  // size), keyed by columnKey.
  perVariantValues?: Record<string, string>;
}

// Warm palette to match the shop (prototype fgm tokens).
const LINE = "#e2d8d3";
const ROW_LINE = "#efe7e1";
const CHECK_GREEN = "#3d8b5f";

// Benefit labels are sentences while the value cells are mostly
// checkmarks, so the label column gets a larger share of the row.
const LABEL_COLUMN_FLEX = 1.6;

// react-native-web Text defaults to word-wrap: break-word, which splits
// a long word ("configuration") at an arbitrary character once it is
// wider than the cell. Prefer dictionary hyphenation instead; the hard
// break remains only as the last resort. Web-only CSS — this screen is
// web-only.
const HYPHENATE = {
  hyphens: "auto",
  WebkitHyphens: "auto",
} as unknown as object;

const stateColor = (state: string) =>
  state === "not-included" ? "#6d6264" : state === "limited" ? "#b45309" : "#251f20";

const ComparisonMatrixV3 = ({ columns, rows, perVariantValues = {} }: Props) => {
  if (columns.length === 0 || rows.length === 0) return null;

  return (
    <View testID="v3-comparison-matrix">
      <View
        className="flex-row pb-2"
        style={{ borderBottomWidth: 1, borderBottomColor: LINE }}
      >
        <View style={{ flex: LABEL_COLUMN_FLEX }} />
        {columns.map((column) => (
          <View key={column.key} className="flex-1 items-center px-1">
            <Text category="s2" style={[{ textAlign: "center" }, HYPHENATE]}>
              {column.label}
            </Text>
          </View>
        ))}
      </View>
      {rows.map((row) => (
        <View
          key={row._key}
          className="flex-row items-center py-2"
          style={{ borderBottomWidth: 1, borderBottomColor: ROW_LINE }}
        >
          <View className="pr-2" style={{ flex: LABEL_COLUMN_FLEX }}>
            <Text category="s2" style={HYPHENATE}>
              {row.label}
            </Text>
          </View>
          {columns.map((column) => {
            const cell = row.cells?.find((c) => c.columnKey === column.key);
            if (!cell) return <View key={column.key} className="flex-1" />;
            const value = cell.perVariant
              ? perVariantValues[column.key] ?? cell.value
              : cell.value;
            // A bare inclusion (no value, or the literal word "Included")
            // reads faster as a green checkmark than as text.
            const showCheck =
              cell.state === "included" &&
              (!value || value.trim().toLowerCase() === "included");
            return (
              <View key={column.key} className="flex-1 items-center px-1">
                {showCheck ? (
                  <Text
                    category="s1"
                    accessibilityLabel="Included"
                    style={{ color: CHECK_GREEN, textAlign: "center" }}
                  >
                    ✓
                  </Text>
                ) : (
                  <Text
                    category="p2"
                    style={[
                      {
                        color: stateColor(cell.state),
                        textAlign: "center",
                      },
                      HYPHENATE,
                    ]}
                  >
                    {cell.state === "not-included" ? "—" : value ?? "Included"}
                  </Text>
                )}
                {cell.note ? (
                  <Text
                    category="c1"
                    appearance="hint"
                    style={[{ textAlign: "center" }, HYPHENATE]}
                  >
                    {cell.note}
                  </Text>
                ) : null}
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
};

export default ComparisonMatrixV3;
